import { FC } from 'react';
import { useSelector } from 'react-redux';

import { BetActionErrorResponseDataDto } from 'api/common.generated';
import { createDictionarySelector } from 'features/dictionaries/store/selectors';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Warning from 'shared/components/warning';
import { BalanceState, BETSType, State } from 'shared/interface';
import { AKO, MAIN_BALANCE, SYSTEM } from '../../../shared/constants/common';
import { getUserDetails, purifyStringFromBoxBrackets } from '../../../shared/util/utility';
import { BetslipErrors } from '../interface';
import {
	dangerousBetslipErrors,
	getAllowedRegMethodName,
	getBetslipErrorObj,
	getEventTypeName,
	getSlipTypeName,
	TAXABLE_AKO_BONUS_VALUE,
	TAXABLE_WIN_VALUE,
} from '../utility';

interface Props {
	errorCodes: number[];
	betStatics?: any;
	onCloseError: (error: number, indexOfErrorDetail: number) => void;
	scrollToBottom: () => void;
	betActionErrors?: BetActionErrorResponseDataDto[];
}

const ErrorCodes: FC<Props> = ({ errorCodes, betStatics, scrollToBottom, onCloseError, betActionErrors }) => {
	const { t } = useTranslation();
	const winningValue = betStatics?.possibleWinning?.winning;
	const bonusValue = betStatics?.possibleWinning?.bonus;
	const [isWarning, setIsWarning] = useState(false);

	const bets = useSelector<State, BETSType>((state) => state.coupon.bets);
	const isFreeBetSelected = useSelector<State, any>((state) => state.coupon.bets.isFreeBetSelected);
	const selectedBalanceType = useSelector<State, BalanceState>((state) => state.cpPanel.balanceData);

	const userDetails = useMemo(() => getUserDetails(), []);
	const isUserVip = userDetails?.vip;
	const isMainBalanceSelected = selectedBalanceType.selectedBalanceType === MAIN_BALANCE;

	const isVipTaxInfoAvailable = isUserVip && bets.betMode === AKO && !isFreeBetSelected && isMainBalanceSelected;

	const dictionarySelector = useSelector(createDictionarySelector);
	const getCategoryNames = (categoryId: number[]) => dictionarySelector('categories', categoryId);

	const isSystemBetTab = bets.betMode === SYSTEM;
	const isGenericError = betStatics?.possibleWinning?.tax > 0;
	const isBonusLessThanThreshold = winningValue > TAXABLE_WIN_VALUE && bonusValue <= TAXABLE_AKO_BONUS_VALUE;
	const isBonusMoreThanThreshold = winningValue <= TAXABLE_WIN_VALUE && bonusValue > TAXABLE_AKO_BONUS_VALUE;
	const isWinningAndBonusMoreThanThreshold = winningValue > TAXABLE_WIN_VALUE && bonusValue > TAXABLE_AKO_BONUS_VALUE;
	const akoWarningMessages = isVipTaxInfoAvailable || isBonusLessThanThreshold || isBonusMoreThanThreshold || isWinningAndBonusMoreThanThreshold;
	const warningMessages = isSystemBetTab ? isGenericError : akoWarningMessages;

	const getErrorMessageFromErrorCode = (errorCode: number) => {
		const detail = betActionErrors?.find((err) => err.betSlipErrorCode === errorCode);

		if (errorCode === BetslipErrors.STAKE_TOO_LOW) {
			return getBetslipErrorObj(t, errorCode, {
				0: detail?.errorDetails?.currencyMinimalStake || detail?.errorDetails?.minStake || detail?.errorDetails?.expected,
			});
		}

		if (errorCode === BetslipErrors.STAKE_LIMIT_REACHED_SINGLE) {
			return getBetslipErrorObj(t, errorCode, { 0: detail?.errorDetails?.colBD });
		}

		if (errorCode === BetslipErrors.SAME_SLIP_IN_SHORT_PERIOD_OF_TIME) {
			return getBetslipErrorObj(t, errorCode, { 0: 'TIME ' });
		}

		if (errorCode === BetslipErrors.CANNOT_COMBINE_CATEGORY) {
			return getBetslipErrorObj(t, errorCode, { 0: detail?.errorDetails?.conflictedCategoryNames });
		}

		if (errorCode === BetslipErrors.ODDS_TOO_LOW) {
			return getBetslipErrorObj(t, errorCode, { 0: detail?.errorDetails?.expected });
		}

		if (errorCode === BetslipErrors.ODDS_PRODUCT_TOO_HIGH) {
			return getBetslipErrorObj(t, errorCode, { 0: detail?.errorDetails?.expected || detail?.errorDetails?.maxOddsProduct });
		}

		if (errorCode === BetslipErrors.TOO_FEW_OUTCOMES) {
			return getBetslipErrorObj(t, errorCode, { 0: detail?.errorDetails?.minOutcomes });
		}

		if (errorCode === BetslipErrors.TOO_MANY_OUTCOMES) {
			return getBetslipErrorObj(t, errorCode, { 0: detail?.errorDetails?.maxOutcomes });
		}

		if (errorCode === BetslipErrors.WRONG_CATEGORY || errorCode === BetslipErrors.FORBIDDEN_CATEGORY) {
			const categoryIds = purifyStringFromBoxBrackets(detail?.errorDetails?.expected || detail?.errorDetails?.disallowedValues)?.split(',');
			return getBetslipErrorObj(t, errorCode, { 0: getCategoryNames(categoryIds?.map((categoryId) => Number(categoryId)) || []) });
		}

		if (errorCode === BetslipErrors.WRONG_EVENT_TYPE) {
			const expectedEventType = purifyStringFromBoxBrackets(detail?.errorDetails?.expected);
			return getBetslipErrorObj(t, errorCode, {
				0: getEventTypeName(t, expectedEventType),
				1: getEventTypeName(t, detail?.errorDetails?.current),
			});
		}

		if (errorCode === BetslipErrors.CUSTOMER_OUTCOME_POSSIBLE_RETURN_LIMIT_REACHED) {
			return getBetslipErrorObj(t, errorCode, { 0: detail?.errorDetails?.customerOutcomePossibleReturnLimit });
		}

		if (errorCode === BetslipErrors.ODDS_PRODUCT_TOO_LOW) {
			return getBetslipErrorObj(t, errorCode, { 0: detail?.errorDetails?.expected || detail?.errorDetails?.minOddsProduct });
		}

		if (errorCode === BetslipErrors.CUSTOMER_DAILY_STAKE_LIMIT_REACHED) {
			if (detail?.referenceObject === 'stake=0.0') {
				return { message: t('error56_dailyCustomerLimitReachedToZero'), code: errorCode };
			}

			const stakeAmountLeft = String(detail?.referenceObject).split('=')[1]; // referenceObject value in response is for eg. { referenceObject: "stake=100.0" }
			return getBetslipErrorObj(t, errorCode, { 0: Number(stakeAmountLeft).toFixed(2) });
		}

		if (errorCode === BetslipErrors.DISALLOWED_REG_METHOD) {
			return getBetslipErrorObj(t, errorCode, { 0: getAllowedRegMethodName(t, detail?.errorDetails?.allowedRegisterMethods) });
		}

		if (errorCode === BetslipErrors.OFFER_NOT_AVAILABLE_FOR_BET_TYPE) {
			return getBetslipErrorObj(t, errorCode, { 0: getSlipTypeName(t, detail?.errorDetails?.expected) });
		}

		if (errorCode === BetslipErrors.OFFER_STAKE_TOO_HIGH) {
			return getBetslipErrorObj(t, errorCode, { 0: detail?.errorDetails?.maxStake });
		}

		if (errorCode === BetslipErrors.OFFER_CONFIGURED_FOR_SPECIFIC_EVENT_TYPES) {
			return getBetslipErrorObj(t, errorCode, { 0: getEventTypeName(t, detail?.errorDetails?.requiredEventType) });
		}

		if (errorCode === BetslipErrors.OFFER_CONFIGURED_FOR_SPECIFIC_CATEGORY) {
			const categoryIds = purifyStringFromBoxBrackets(detail?.errorDetails?.requiredCategory)?.split(',');
			return getBetslipErrorObj(t, errorCode, { 0: getCategoryNames(categoryIds?.map((categoryId) => Number(categoryId)) || []) });
		}

		if (errorCode === BetslipErrors.MAX_POSSIBLE_RETURN) {
			return getBetslipErrorObj(t, errorCode, { 0: detail?.errorDetails?.MAX_POSSIBLE_RETURN || detail?.errorDetails?.expected });
		}

		if (errorCode === BetslipErrors.FORBIDDEN_EVENT) {
			const eventIds = purifyStringFromBoxBrackets(detail?.errorDetails?.expected || detail?.errorDetails?.disallowedValues)?.split(',');
			const eventName = bets.outcomes.find((outcome) => eventIds?.includes(String(outcome.eventId)))?.eventName;
			return getBetslipErrorObj(t, errorCode, { 0: eventName });
		}

		return getBetslipErrorObj(t, errorCode);
	};

	// creating new errorCode obj because there might be same error codes for different events. We need to show only one error per errorCode.
	const uniqueErrorCodes = errorCodes.filter((errorCode, index) => errorCodes.indexOf(errorCode) === index);
	const errMessages = uniqueErrorCodes.map((errorCode) => getErrorMessageFromErrorCode(errorCode));

	useEffect(() => {
		if (errorCodes.length > 0 || isWarning) {
			scrollToBottom();
		}
	}, [errorCodes.length, isWarning, scrollToBottom]);

	useEffect(() => {
		setIsWarning(warningMessages);
	}, [betStatics]);

	const renderWarningDescription = (): any => {
		if (isSystemBetTab && isGenericError) {
			return t('plnMessage');
		}
		if (isVipTaxInfoAvailable) {
			return t('vipInfoForTax');
		}

		if (isBonusLessThanThreshold) {
			return t('bonusLessThanThresholdMessage');
		}

		if (isBonusMoreThanThreshold) {
			return t('bonusMoreThanThresholdMessage');
		}
		if (isWinningAndBonusMoreThanThreshold) {
			return t('winningAndBonusMoreThanThresholdMessage');
		}
	};

	const warningContent = {
		type: isVipTaxInfoAvailable ? 'information' : 'warning',
		title: t('information'),
		desc: renderWarningDescription(),
	};

	return (
		<>
			{errMessages.map((message, index) => (
				<Warning
					key={`errorCode-${index}`}
					type={dangerousBetslipErrors.includes(message.code) ? 'error' : 'warning'}
					title={t('attention')}
					desc={message.message}
					onClose={() => onCloseError(message.code, index)}
					cyTestRef="betslip-error"
				/>
			))}
			{isWarning && <Warning {...warningContent} onClose={() => setIsWarning(false)} cyTestRef="betslip-error" />}
		</>
	);
};

export default ErrorCodes;
